import type { SessionSnapshot, ParticipantInfo, PollQuestion, SessionKind } from "@shared/types";

const json = { "Content-Type": "application/json" };

export interface PersistentSession {
  id: string;
  title: string;
  sessionLabel: string;
  kind: SessionKind;
  joinCode: string;
  status: string;
}

// The two long-lived sessions (Live + Sandbox) offered on the start screen.
export async function listSessions(): Promise<PersistentSession[]> {
  const r = await fetch("/api/sessions");
  if (!r.ok) throw new Error("Failed to load sessions");
  const data = await r.json();
  return data.sessions ?? [];
}

export interface CreatedSession {
  session: SessionSnapshot["session"];
  facilitatorToken: string;
}

// Admin key (held in localStorage after the admin unlocks the section) is sent
// with admin-only requests as a header.
function adminHeaders(): Record<string, string> {
  const key = localStorage.getItem("adminKey") || "";
  return key ? { ...json, "X-Admin-Key": key } : json;
}

export async function verifyAdmin(key: string): Promise<boolean> {
  const r = await fetch("/api/admin/verify", { headers: { "X-Admin-Key": key } });
  return r.ok;
}

export async function resetSession(sessionId: string): Promise<void> {
  const r = await fetch(`/api/sessions/${sessionId}/reset`, {
    method: "POST",
    headers: adminHeaders(),
  });
  if (!r.ok) throw new Error(r.status === 401 ? "Admin key rejected" : "Reset failed");
}

export async function createSession(input: {
  title?: string;
  client?: string;
  facilitator?: string;
  sessionLabel?: string;
  meetingLink?: string | null;
  expectedParticipants?: number | null;
}): Promise<CreatedSession> {
  const r = await fetch("/api/sessions", { method: "POST", headers: adminHeaders(), body: JSON.stringify(input) });
  if (!r.ok) throw new Error(r.status === 401 ? "Admin key rejected" : "Failed to create session");
  return r.json();
}

export async function fetchSnapshot(sessionId: string): Promise<SessionSnapshot> {
  const r = await fetch(`/api/sessions/${sessionId}`);
  if (!r.ok) throw new Error("Session not found");
  return r.json();
}

export interface JoinResolve {
  sessionId: string;
  session: { id: string; title: string; client: string };
  questions: PollQuestion[];
  vectors: { index: number; shortLabel: string; color: string }[];
}

export async function resolveJoinCode(code: string): Promise<JoinResolve> {
  const r = await fetch(`/api/join/${encodeURIComponent(code)}`);
  if (!r.ok) throw new Error("Invalid join code");
  return r.json();
}

export async function joinSession(
  sessionId: string,
  input: { displayName?: string; department?: string; role?: string }
): Promise<ParticipantInfo> {
  const r = await fetch(`/api/sessions/${sessionId}/participants`, {
    method: "POST",
    headers: json,
    body: JSON.stringify(input),
  });
  if (!r.ok) throw new Error("Failed to join");
  const data = await r.json();
  return data.participant;
}

export async function ingestCsv(
  sessionId: string,
  file: File,
  source?: string
): Promise<{ rowCount: number; schema?: string }> {
  const form = new FormData();
  form.append("file", file);
  if (source) form.append("source", source);
  const r = await fetch(`/api/sessions/${sessionId}/ingest`, { method: "POST", body: form });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || "Ingestion failed");
  }
  return r.json();
}

export async function fetchBatches(sessionId: string): Promise<
  { id: string; source: string; filename: string; rowCount: number; createdAt: string }[]
> {
  const r = await fetch(`/api/sessions/${sessionId}/batches`);
  if (!r.ok) return [];
  const data = await r.json();
  return data.batches ?? [];
}

export function reportUrl(sessionId: string): string {
  return `/api/sessions/${sessionId}/report`;
}
